import { useState } from "react";
import { useLanguage } from "../../../i18n/LanguageContext";

export default function FlashcardExercise({
  exercise,
  submittingLearningExercise,
  submitLearningExercise,
  styles,
}) {
  const { language } = useLanguage();

  // =========================================================
  // Canonical data
  // These remain the source data.
  // =========================================================
  const front =
    exercise.exercise_data?.front ||
    exercise.prompt ||
    "";

  const back =
    exercise.exercise_data?.back ||
    "";

  // =========================================================
  // Display-only ES / RU translations
  // =========================================================
  const translatedData =
    exercise.translated_exercise_data || {};

  const translatedFront =
    language === "es" || language === "ru"
      ? translatedData.front || null
      : null;

  const translatedBack =
    language === "es" || language === "ru"
      ? translatedData.back || null
      : null;

  const translatedPrompt =
    language === "es" || language === "ru"
      ? exercise.translated_prompt || null
      : null;

  // =========================================================
  // Separate legacy mixed English + Chinese display text
  // =========================================================
  const splitEnglishChinese = (text = "") => {
    const firstChineseIndex = String(text).search(
      /[\u3400-\u4DBF\u4E00-\u9FFF]/
    );

    if (firstChineseIndex === -1) {
      return {
        english: String(text).trim(),
        chinese: null,
      };
    }

    return {
      english: String(text)
        .slice(0, firstChineseIndex)
        .trim(),

      chinese: String(text)
        .slice(firstChineseIndex)
        .trim(),
    };
  };

  const promptParts =
    splitEnglishChinese(exercise.prompt || "");

  const frontParts =
    splitEnglishChinese(front);

  const backParts =
    splitEnglishChinese(back);

  const englishPrompt = promptParts.english;
  const chinesePrompt = promptParts.chinese;

  const englishFront = frontParts.english;
  const chineseFront = frontParts.chinese;

  const englishBack = backParts.english;
  const chineseBack = backParts.chinese;

  // =========================================================
  // State
  // =========================================================
  const [showBack, setShowBack] =
    useState(false);

  const [completed, setCompleted] =
    useState(false);

  // =========================================================
  // Review result
  // Existing remembered boolean behavior remains unchanged.
  // =========================================================
  const handleComplete = async (remembered) => {
    const success =
      await submitLearningExercise({
        exerciseId: exercise.id,
        answer: {
          remembered,
        },
        isCorrect: remembered,
      });

    if (!success) return;

    setCompleted(true);
  };

  const handleTryAgain = () => {
    setShowBack(false);
    setCompleted(false);
  };

  return (
    <div
      style={{
        ...styles.emptyBox,
        marginBottom: "18px",
      }}
    >
      <div
        style={{
          fontWeight: "bold",
          marginBottom: "8px",
        }}
      >
        Flashcard
      </div>

      {/* =====================================================
          Prompt
          EN: English only
          ZH: English + Chinese
          ES/RU: English + selected translation
      ====================================================== */}

      {englishPrompt &&
        englishPrompt !== englishFront && (
          <div style={styles.quizPath}>
            {englishPrompt}
          </div>
        )}

      {language === "zh" &&
        chinesePrompt &&
        chinesePrompt !== chineseFront && (
          <div
            style={{
              ...styles.quizPath,
              marginTop: "4px",
              color: "#6b7280",
            }}
          >
            {chinesePrompt}
          </div>
        )}

      {(language === "es" ||
        language === "ru") &&
        translatedPrompt && (
          <div
            style={{
              ...styles.quizPath,
              marginTop: "4px",
              color: "#6b7280",
            }}
          >
            {translatedPrompt}
          </div>
        )}

      {/* =====================================================
          Flashcard
      ====================================================== */}

      <div
        style={{
          marginTop: "16px",
          padding: "28px",
          border: "1px solid #d1d5db",
          borderRadius: "16px",
          backgroundColor: "#ffffff",
          textAlign: "center",
          minHeight: "160px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          cursor: completed
            ? "default"
            : "pointer",
        }}
        onClick={() => {
          if (!completed) {
            setShowBack((prev) => !prev);
          }
        }}
      >
        {!showBack ? (
          <>
            {/* Canonical English front */}
            <div
              style={{
                fontSize: "20px",
                fontWeight: "bold",
              }}
            >
              {englishFront}
            </div>

            {/* Chinese front */}
            {language === "zh" &&
              chineseFront && (
                <div
                  style={{
                    marginTop: "8px",
                    color: "#6b7280",
                    fontSize: "17px",
                    lineHeight: "1.5",
                  }}
                >
                  {chineseFront}
                </div>
              )}

            {/* Spanish / Russian front */}
            {(language === "es" ||
              language === "ru") &&
              translatedFront && (
                <div
                  style={{
                    marginTop: "8px",
                    color: "#6b7280",
                    fontSize: "17px",
                    lineHeight: "1.5",
                  }}
                >
                  {translatedFront}
                </div>
              )}

            <div
              style={{
                marginTop: "14px",
                color: "#6b7280",
                fontSize: "14px",
              }}
            >
              Click to reveal answer
            </div>
          </>
        ) : (
          <>
            {/* Canonical English back */}
            <div
              style={{
                fontSize: "20px",
                fontWeight: "bold",
              }}
            >
              {englishBack}
            </div>

            {/* Chinese back */}
            {language === "zh" &&
              chineseBack && (
                <div
                  style={{
                    marginTop: "8px",
                    color: "#6b7280",
                    fontSize: "17px",
                    lineHeight: "1.5",
                  }}
                >
                  {chineseBack}
                </div>
              )}

            {/* Spanish / Russian back */}
            {(language === "es" ||
              language === "ru") &&
              translatedBack && (
                <div
                  style={{
                    marginTop: "8px",
                    color: "#6b7280",
                    fontSize: "17px",
                    lineHeight: "1.5",
                  }}
                >
                  {translatedBack}
                </div>
              )}

            <div
              style={{
                marginTop: "14px",
                color: "#6b7280",
                fontSize: "14px",
              }}
            >
              Click again to hide
            </div>
          </>
        )}
      </div>

      {/* =====================================================
          Review buttons
      ====================================================== */}

      {showBack && !completed && (
        <div
          style={{
            marginTop: "16px",
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          <button
            style={styles.smallSecondaryButton}
            disabled={submittingLearningExercise}
            onClick={() =>
              handleComplete(false)
            }
          >
            Need Review
          </button>

          <button
            style={styles.smallPrimaryButton}
            disabled={submittingLearningExercise}
            onClick={() =>
              handleComplete(true)
            }
          >
            I Remembered
          </button>
        </div>
      )}

      {completed && (
        <div style={{ marginTop: "16px" }}>
          <div
            style={{
              color: "#16a34a",
              fontWeight: "bold",
            }}
          >
            Review recorded.
          </div>

          <button
            style={{
              ...styles.smallSecondaryButton,
              marginTop: "10px",
            }}
            onClick={handleTryAgain}
          >
            Review Again
          </button>
        </div>
      )}
    </div>
  );
}